import type { Role } from '@/features/auth/api';

/**
 * 各マニュアルの冒頭に出す「全体の流れ」。
 *
 * 個々の手順の前に、できあがるまでのステップと「そのあと何が起きるか」を
 * 1本の流れで見せる。ステップの文言は画面に出ているボタン名・見出しに合わせる。
 */
export interface HelpFlow {
  /** 流れの見出し（省略時は「全体の流れ」） */
  title?: string;
  /** この流れを表示するロール。省略時は全ロール */
  roles?: Role[];
  /** 完成までのステップ（左から右へ） */
  steps: string[];
  /** 最後のステップのあと起きること */
  after?: string;
}

const OFFICE: Role[] = ['office'];
const STUDENT: Role[] = ['student'];
const DIRECTOR: Role[] = ['director'];
const SUCCESS: Role[] = ['success'];
const SUPER_ADMIN: Role[] = ['super_admin'];
const OFFICE_AND_SUPER: Role[] = ['office', 'super_admin'];

export const HELP_FLOWS: Record<string, HelpFlow[]> = {
  dashboard: [
    {
      steps: [
        'ログインする',
        '画面のいちばん上に出ている「いまやるべきこと」を読む',
        '青い文字（リンク）をクリックして、その作業の画面を開く',
        '作業が終わったら、もう一度ホームに戻って残っていないか確認する',
      ],
      after:
        '終わった作業は、ホームの一覧から消えるか件数が減ります。赤い丸は「まだ読んでいないお知らせ」です。作業の残り件数ではありません。',
    },
  ],

  members: [
    {
      title: '受講生をシステムに迎え入れるまで',
      roles: OFFICE,
      steps: [
        '左メニュー「業務マスター」で「期」と「コース」を登録する',
        '左メニュー「会員管理」→「会員一覧」の右上「招待する」から1人ずつ登録する（または「会員CSV取込」でまとめて登録する）',
        '招待メールを受け取った本人が、案内のリンクからパスワードなどを設定してログインできるようになる',
        'CSVだけで登録した人は、会員の詳細画面からあとで「招待する」',
      ],
      after:
        '名前や会社名の直すところは「プロフィールを編集」、Meet用メールなどは「連携設定を編集」です。卒業・退会も会員の詳細画面で行います。パスワードを忘れた受講生の復旧も同じ画面の「ログイン情報」です。',
    },
    {
      roles: ['director', 'success'],
      steps: [
        '左メニュー「関わった会員」を開く（自分が担当した回の対象になった人だけが出ます）',
        '行の「詳細」で、公開プロフィール（顔写真・会社名・自己紹介など）を読む',
        '提出物・出欠・アンケートの回答を見るときは、同じ行の「年間ポートフォリオ」を開く',
      ],
      after:
        '電話番号や連絡用メールなど、相手に直接連絡する情報は出ません。連絡が必要なときは事務局へ頼んでください。',
    },
    {
      roles: STUDENT,
      steps: [
        '左メニュー「受講生一覧」を開く',
        'ほかの受講生の名前をクリックして、公開されているプロフィールを見る',
        '自分の名前・顔写真・会社名などを直したいときは左メニュー「マイプロフィール」を開く',
      ],
      after: '自分の電話番号などは、ほかの受講生には見えません。',
    },
  ],

  profile: [
    {
      roles: STUDENT,
      steps: [
        '左メニュー「マイプロフィール」を開く',
        '顔写真・自己紹介・連絡先などを入力する',
        '「保存」をクリックする（写真はファイルを選んだ時点で反映されます）',
      ],
      after: '登録した内容は、理事・講師やサクセス担当が指導の参考にします。',
    },
    {
      roles: ['director', 'success', 'office', 'super_admin'],
      steps: [
        '画面右上の自分の名前をクリックし、「アカウント設定」を選ぶ（左メニューからも開けます）',
        '顔写真・会社名・役職・連絡先・自己紹介を入力する',
        '「プロフィールを保存する」をクリックする',
      ],
      after: '受講生やほかの担当者から見える紹介情報になります。',
    },
  ],

  portfolio: [
    {
      roles: STUDENT,
      steps: [
        '左メニュー「年間ポートフォリオ」を開く',
        '年度（4月から翌3月）を確認する',
        '出欠・提出した課題やアンケート・採点・社長アンケートを1画面で読む',
      ],
      after: 'ほかの受講生のポートフォリオは開けません。',
    },
    {
      roles: OFFICE,
      steps: [
        '会員の詳細画面を開く',
        '氏名の下の「この会員:」から「年間ポートフォリオ」をクリックする',
        'その受講生の1年分の記録を読む',
      ],
      after: '点数の内訳まで見えるのは、本人と事務局だけです。',
    },
    {
      roles: ['director', 'success'],
      steps: [
        '左メニュー「関わった会員」を開く',
        '見たい人の行の「年間ポートフォリオ」をクリックする',
        'その受講生の1年分の記録を読む',
      ],
      after:
        '会員詳細（プロフィール）からは入れません。点数の内訳は出ません（本人と事務局だけです）。',
    },
  ],

  events: [
    {
      title: '月例会などのイベントを開催するまで',
      roles: OFFICE,
      steps: [
        '左メニュー「イベント管理」の下から、作りたい種類（月例会・分科会・EXゼミなど）をクリックする',
        '右上の「（種類の名前）の新規登録」からタイトル・日時・会場・定員などを入れる。画面下に「この回のメール」が見積もりで出る。登録する（この時点では下書き＝受講生には見えない。メール予約はここで入る）',
        '「対象者」タブで、誰が参加するかを決めて保存する',
        '「担当者」タブで、この回の理事・講師やサクセス担当を選んで保存する',
        '必要なら、課題・アンケートの各タブでフォームを割り当てる（タブの名前は種類によって変わります）',
        '「通知」タブで、この回のメールの時刻・追加・キャンセルを確認する',
        '右側の「公開する」をクリックし、確認画面でもう一度「公開する」',
      ],
      after:
        '公開すると対象の受講生に見えます。お知らせメールの予約は登録時に入っており、公開前は時刻が来ても送らずスキップします。当日は「出欠」（出欠を取る回のみ）「当日写真」、終わったら右側から「終了」に変えます。月例会は出欠を取りません。サクセス面談・1on1は出欠も課題も使いません。席次はシステムでは組まず、出欠一覧のCSVを使って手元で作ります。',
    },
    {
      roles: STUDENT,
      steps: [
        '公開されると、ホームと左メニュー「イベント」に自分の回だけが出る',
        'イベント名をクリックして詳細を開く',
        '「出欠」タブがある回は、参加・欠席などを締切までに答える（月例会にはこのタブがありません）',
        '事前の課題やアンケートがあれば期限内に提出する',
        '終わったあと、事後の課題やアンケートがあれば提出する',
      ],
      after: 'まだやっていないことは、ホームの「次にやること」にも出ます。そこからも同じ画面を開けます。',
    },
    {
      roles: DIRECTOR,
      steps: [
        '公開されると、左メニュー「担当イベント」に自分が担当の回が出る',
        'イベント名をクリックして詳細を開く',
        '「出欠」タブがある回では、誰が来るか（出席者の名前）を確認する',
        '当日のあとは「当日写真」タブに写真を載せる',
        '分科会などのあと、案内に従って優秀者や態度の入力をする',
      ],
      after: '入力した内容は、毎月の採点の材料になります。出欠を直せるのは事務局だけです。',
    },
    {
      roles: SUCCESS,
      steps: [
        '左メニュー「イベント」を開く（自分が担当した回だけが出ます。種類ごとの一覧も下に並びます）',
        'サクセス面談なら、対象の受講生と日時を確認する',
        '日時が「日程調整中」なら、受講生が「実施可能日」から選ぶのを待つ（自分の空きは左メニュー「実施可能日」で登録）',
        '面談が終わったら「面談記録」タブに記録を書く',
      ],
      after: '月例会などに担当として入っている回も、同じ一覧から開けます。',
    },
  ],

  scores: [
    {
      roles: OFFICE,
      steps: [
        '左メニュー「採点」→「採点管理（CSV・公開）」を開く',
        '対象の月（またはCSVに書いてある期間）を確認する',
        '必要なら「生データ」をCSVで書き出す',
        '表計算ソフトで点数を計算したCSVを取り込む',
        '内容を確認して「公開する」',
      ],
      after:
        '公開すると受講生が順位を見られます。点数の内訳は本人と事務局だけです。間違えたときは公開を取り消して直前の版に戻せます。',
    },
    {
      roles: STUDENT,
      steps: [
        '左メニュー「採点結果」を開く',
        '見たい月（期間）を選ぶ',
        '順位の一覧を読む',
      ],
      after: '自分の行から点数の内訳も見られます。ほかの人の内訳は見えません。',
    },
  ],

  'office-accounts': [
    {
      roles: OFFICE,
      steps: [
        '左メニュー「会員管理」→「事務局アカウント」を開く',
        '管理責任者だけに見える右上の「招待する」から、新しい事務局のメールアドレスを入れる',
        '相手が案内のリンクからログイン設定を終えると、一覧に名前が出る',
        '必要なら「権限区分を切替」で管理責任者／一般を変える',
      ],
      after:
        '辞めた人は「有効・停止を切替」でログインできなくします。最後の管理責任者は停止できません。',
    },
  ],

  staff: [
    {
      roles: OFFICE,
      steps: [
        '左メニュー「会員管理」→「理事・講師」または「サクセス担当」を開く',
        '右上の「招待する」でメールアドレスと、分かっているプロフィールを入れて送る',
        '相手が受諾すると一覧に出る',
        'イベント詳細の「担当者」タブで、その回の担当に選ぶ',
      ],
      after:
        '受講生に固定の担任は付けません。誰がその受講生を見られるかは、イベントの担当者で決まります。退任したら一覧で「停止」します。',
    },
  ],

  masters: [
    {
      roles: OFFICE,
      title: '全体の流れ',
      steps: [
        '左メニュー「業務マスター」を開く',
        'カードの「ここでの登録が出てくる場所」を読む',
        '期・コース・イベント種別・アンケートテンプレート・席次NG・通知テンプレート・通知プリセットの、必要な画面を開く',
        '項目を登録する',
        'そのあと会員招待やイベント作成の画面で、今登録した選択肢が出ることを確認する',
      ],
      after:
        'マスターを後から直しても、すでに作った会員・イベントの過去の記録は残ります。席次NGは、今は席次をシステムで組まないため、登録しても効く画面がありません（控えとして残す欄です）。',
    },
  ],

  availability: [
    {
      roles: SUCCESS,
      steps: [
        '左メニュー「実施可能日」を開く',
        '面談できる日時を登録する',
        '事務局が日時未定のサクセス面談を作る',
        '受講生が、空いている日時から1つ選んで確定する',
      ],
      after: '選ばれた日時は、ほかの面談の候補からは消えます。',
    },
  ],

  'notification-setup': [
    {
      roles: OFFICE,
      title: '自動配信メールが届くまでの流れ',
      steps: [
        '「通知テンプレート」で文面（件名・本文）を用意する',
        '「通知プリセット」で、その文面を「誰に・いつ送るか」のルールに組み込む',
        'そのイベント種別でイベントを作る',
        '作った時点で、ルールの本数だけ配信予定が自動で入る',
        'イベントを公開する',
        '予定の時刻になると、システムが自動で送る',
      ],
      after:
        'プリセットを直しても、すでに作ったイベントの配信予定は作り直されません。直せるのは「これから作る回」だけです。すでに作った回を直すときは、イベント詳細の「通知」タブでこの回だけ変更します。',
    },
  ],

  notifications: [
    {
      roles: STUDENT,
      steps: [
        'お知らせはメールと、このシステムの画面の両方に届きます',
        '画面右上のベル、または左メニュー「システム通知」を開く',
        '件名をクリックして本文を読む',
        '本文の案内どおり、イベントやメッセージの画面へ進む',
      ],
      after: '開いて読むと未読の赤い丸が減ります。',
    },
    {
      roles: OFFICE,
      steps: [
        'イベントを作った時点で、配信の予約が自動で入ります（公開前は送る時刻になってもスキップされます）',
        '新規登録画面の「この回のメール」、またはイベント詳細の「通知」タブで内容と時刻を確認する。この回だけ時刻変更・追加・キャンセルできる',
        '左メニュー「システム通知（配信予定・履歴）」では、イベントをまたいで確認できる',
        '1回限りの全体お知らせは、手入力の配信から作れます',
      ],
      after:
        '公開が遅れて予約時刻を過ぎた分は送られません。サクセス面談を日時未定で作った場合、日時が決まるまで一部のリマインド予約は作られません。',
    },
  ],

  president: [
    {
      roles: OFFICE,
      steps: [
        '会員の詳細の「この会員:」から「社長アンケート」を開き、1人ずつ入力する',
        '人数が多いときは左メニュー「会員管理」→「社長アンケートCSV取込」で1人1行の表を取り込む',
      ],
      after:
        '登録した内容は事務局だけが見られます。誰に見せるかを選ぶ欄はありません（機微な情報のため、システム側で固定しています）。',
    },
    {
      roles: STUDENT,
      steps: [
        '左メニュー「社長からの期待」を開く',
        '自分に開示された内容だけを読む',
      ],
      after:
        '入力するのは事務局です。今の運用では事務局だけが見る扱いのため、ここには何も出ないことがあります。',
    },
    {
      roles: DIRECTOR,
      steps: ['会員の詳細で「社長アンケート」の欄を開く', '開示されている内容を読む'],
      after: '今の運用で新しく登録されたものは事務局だけが見るため、ここには出ません。',
    },
  ],

  surveys: [
    {
      title: 'アンケートや課題が受講生に届くまで',
      roles: OFFICE,
      steps: [
        '左メニュー「業務マスター」→「アンケートテンプレート」で、使うイベント種別を選んで質問のひな型を作る',
        'そのひな型の版を使える状態にする',
        'イベント詳細の課題・アンケートのタブ（名前は種類によって変わります）で割り当て、締切を入れる',
        'イベントを公開する',
        '受講生の回答を、同じタブの提出状況・個別回答で確認する',
      ],
      after:
        '課題・アンケートの独立した一覧メニューはありません。必ずイベントのタブから見ます。サクセス面談には割り当てできません。1on1は事後アンケートだけ割り当てられます。',
    },
    {
      roles: STUDENT,
      steps: [
        'ホームの「次にやること」、通知、またはイベント詳細から開く',
        '「ここから回答する」を押すと、同じタブの中に回答欄が開く',
        '質問に答える（途中まで保存できるものもあります）',
        '「提出する」をクリックする',
      ],
      after: '締切までは直して出し直せることがあります。画面の案内を読んでください。',
    },
  ],

  messages: [
    {
      roles: STUDENT,
      steps: [
        '左メニュー「メッセージ」を開く',
        '事務局に聞きたいことは、連絡先から事務局とのやり取りを開く（または新規）',
        'イベントの連絡は、そのイベントの「グループメッセージ」タブでもできます',
        '本文を書いて送信する。ファイルを添付することもできます',
      ],
      after: '返信はこの画面と、設定していればメールでも分かります。件名はありません。',
    },
    {
      roles: OFFICE,
      steps: [
        '受講生からの問い合わせは左メニュー「メッセージ」→「問い合わせ対応」に入ります',
        '連絡したい相手は「連絡先」から名前を選ぶ（同じ相手とは1本の会話にまとまります）',
        '本文を書いて返信する',
      ],
      after:
        '全員へのお知らせはメッセージではなく「システム通知」の手入力配信を使います。状態や優先度の欄は使いません。',
    },
    {
      roles: ['director', 'success'],
      steps: [
        '左メニュー「メッセージ」を開く',
        'イベントのグループや、事務局とのやり取りを開く',
        '本文を書いて送信する。グループでは @名前 で相手を示せます',
      ],
      after: 'サクセス面談・1on1の連絡も、その回のグループメッセージです。',
    },
  ],

  inbox: [
    {
      roles: OFFICE,
      steps: [
        '左メニュー「メッセージ」→「問い合わせ対応」を開く',
        '未読の行をクリックして内容を読む',
        'スレッド（会話）で返信する',
      ],
      after:
        '開いて読むと赤い丸（未読）が消えます。「対応した件数」ではありません。内部メモは相手に見えません。',
    },
  ],

  success: [
    {
      roles: SUCCESS,
      steps: [
        '「実施可能日」に空き日時を入れる',
        '事務局が作ったサクセス面談のイベントが「イベント」の一覧に出る',
        '受講生が候補日から日時を確定する（確定までは「日程調整中」）',
        '当日、グループメッセージで最終連絡する',
        '終わったらイベントの「面談記録」タブに記録を書く',
      ],
      after: '議事録の公開範囲は、書いたときに選んだ人だけです。',
    },
    {
      roles: OFFICE,
      steps: [
        '左メニュー「イベント管理」→「サクセス面談」から日時未定でも予定を作る',
        '対象の受講生1人と、サクセス担当1人を選ぶ',
        '公開する',
        '必要ならイベントの「面談記録」を確認する',
      ],
      after: '1on1は「1on1」メニューから、理事・講師1人と受講生1人で同様に作ります。',
    },
  ],

  integrations: [
    {
      roles: OFFICE_AND_SUPER,
      steps: [
        '左メニュー「外部連携」を開く',
        '「接続設定」でメール送信（SMTP）や、使う連絡ツールの接続情報を登録する',
        '保存する（パスワードなどの値は画面に再表示されません）',
        '受講生ごとのSlackなどの宛先は、会員詳細の「連携設定を編集」で登録する',
      ],
      after:
        '未設定の項目は、サーバーの設定ファイル側の値があればそれを使います。ファイルの保存先は別サービスではなく、このシステムのディスクです。',
    },
  ],

  'audit-logs': [
    {
      roles: OFFICE_AND_SUPER,
      steps: [
        '左メニュー「監査ログ」を開く',
        '操作の種類や期間で絞り込む',
        '行をクリックすると詳細が別ウィンドウ（モーダル）で出る',
        '必要なら「この人の操作だけ表示」で経緯を追う',
      ],
      after: '記録は消したり書き換えたりできません。',
    },
  ],

  superadmin: [
    {
      roles: SUPER_ADMIN,
      steps: [
        '障害や問い合わせの内容を聞く',
        '「ジョブ・障害対応」や監査ログで状況を確認する',
        '必要なら配信のやり直しや、事務局アカウントの緊急復旧を行う',
      ],
      after: '保守モードの切替も含め、操作はすべて監査ログに残ります。',
    },
  ],
};

/** そのロールに表示する流れだけを返す */
export function flowsFor(topicId: string, role: Role): HelpFlow[] {
  return (HELP_FLOWS[topicId] ?? []).filter((flow) => !flow.roles || flow.roles.includes(role));
}
